var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { bindable } from "aurelia-templating";
import { bindingMode } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { Guid } from "../../../utilities/guid";
import { bioMp, ScreenType } from "../../app/admin/branding/boMp";
import { readTag, tagText } from "../../../utilities/inline-text-marker";
// The brand-level pages the editor can preview, in tab order.
export var RenderPreviewPages = [
    { value: "lobby", labelKey: "render-page-lobby" },
    { value: "lotteries", labelKey: "render-page-lotteries" },
    { value: "bundles", labelKey: "render-page-bundles", requiresCapability: "bundles" },
    { value: "cart", labelKey: "render-page-cart" },
    { value: "results", labelKey: "render-page-results" },
    { value: "purchases", labelKey: "render-page-purchases" }
];
export function visibleRenderPreviewPages(capabilities) {
    return RenderPreviewPages.filter(function (p) { return !p.requiresCapability || !capabilities || capabilities[p.requiresCapability] !== false; });
}
var RenderPreview = /** @class */ (function () {
    function RenderPreview() {
        // Two-way so the in-preview language picker writes the choice back to the host's selected language
        // (which is where inline edits are stored). langChanged() reloads the preview in the new language.
        this.lang = "en";
        // Brand-supported languages for the inline-edit language picker (code + display name + flag image).
        this.langs = [];
        this.page = "lobby";
        this.showPagePicker = false;
        // Brand data capabilities (e.g. { bundles: false }). A page whose capability is KNOWN false is not
        // offered as a tab - an All Bundles page on a brand with no bundles has nothing to show.
        this.capabilities = null;
        this.hideTitle = false;
        // Bundle-scoped preview: when set, the iframe targets the ID-backed bundle route and passes bundleId
        // through so the server resolves that bundle's draft layout + tokens.
        this.bundleId = null;
        this.bundleKey = null;
        // Syndicate-scoped preview: the per-syndicate equivalent of bundleId/bundleKey. The ID-backed syndicate
        // route also needs a brandLotteryId (the subscription the syndicate is loaded under).
        this.syndicateId = null;
        this.syndicateKey = null;
        this.syndicateBrandLotteryId = null;
        // Lottery-scoped preview: per-(brand x lottery). The override is keyed by brandLotteryId (the brand's own
        // instance of the lottery); lotteryId + key are still needed to load the lottery page's data.
        this.lotteryId = null;
        this.lotteryKey = null;
        this.lotteryBrandLotteryId = null;
        // Inline text-edit mode: when true the iframe is loaded with ?editText=true (the app tags every
        // translated string with its i18n key) and clicking a piece of text opens an in-place editor.
        this.editText = false;
        // Host opts in to show the Edit text toggle (the non-editing previews don't).
        this.editable = false;
        // Sub-mode within edit mode: false = Edit (clicks edit text), true = Navigate (clicks pass through to the
        // page so menus/accordions/dialogs can be opened to reveal the text they hide, then flip back to Edit).
        this.navigate = false;
        this.previewPages = visibleRenderPreviewPages(null);
        this.id = Guid.generateGuid();
        this.screenType = ScreenType.Mac;
        // Exposed so the view can compare against the active screen type.
        this.ScreenType = ScreenType;
        this.flashIndex = 0;
    }
    RenderPreview_1 = RenderPreview;
    RenderPreview.prototype.attached = function () {
        this.reload();
    };
    RenderPreview.prototype.brandIdChanged = function () {
        this.reload();
    };
    RenderPreview.prototype.templateNameChanged = function () {
        this.reload();
    };
    RenderPreview.prototype.pageChanged = function () {
        this.reload();
    };
    RenderPreview.prototype.capabilitiesChanged = function () {
        this.previewPages = visibleRenderPreviewPages(this.capabilities);
    };
    RenderPreview.prototype.bundleIdChanged = function () {
        this.reload();
    };
    RenderPreview.prototype.syndicateIdChanged = function () {
        this.reload();
    };
    RenderPreview.prototype.lotteryIdChanged = function () {
        this.reload();
    };
    RenderPreview.prototype.langChanged = function () {
        this.reload();
    };
    RenderPreview.prototype.editTextChanged = function () {
        this.navigate = false; // always (re)enter edit mode in Edit sub-mode
        this.reload(true);
    };
    // Pick the language to preview + edit. Setting lang triggers langChanged() (reload) and, via the two-way
    // binding, updates the host's selected language so inline edits land in that language's tokens.
    RenderPreview.prototype.selectLang = function (code) {
        if (this.lang === code)
            return;
        this.lang = code;
    };
    RenderPreview.prototype.toggleEditText = function () {
        this.editText = !this.editText;
    };
    // Flip between Edit and Navigate sub-modes. No reload: reloading would collapse whatever the admin just
    // revealed in Navigate mode (open accordion / dialog); the click + hover handlers read this.navigate live.
    RenderPreview.prototype.selectEditMode = function (navigate) {
        if (this.navigate === navigate)
            return;
        this.navigate = navigate;
        if (navigate) {
            this.closeInlineEditor();
            this.setHoverEditable(null);
        }
    };
    // The admin clicked a page tab: switch the previewed page, reload, and notify the host so it can
    // follow (e.g. the layout editor swaps to that page's layout document).
    RenderPreview.prototype.selectPage = function (value) {
        if (this.page === value)
            return;
        this.page = value; // triggers pageChanged() → reload() for the new page
        if (this.pageChange)
            this.pageChange({ page: value });
    };
    Object.defineProperty(RenderPreview.prototype, "canPreview", {
        get: function () {
            return this.brandId != null && !!this.templateName;
        },
        enumerable: false,
        configurable: true
    });
    RenderPreview.prototype.selectScreenType = function (type) {
        if (this.screenType === type)
            return;
        this.screenType = type;
        this.reload(true);
    };
    /** Rebuild the preview iframe. Pass force=true after a save / screen-type change. */
    RenderPreview.prototype.reload = function (force) {
        if (force === void 0) { force = false; }
        if (!this.container || !this.canPreview)
            return;
        var theme = this.themeName || "active-theme";
        // Bundle/syndicate preview targets the ID-backed route (preview/<bundle|syndicate>/:id/:key) and
        // threads the id so the server resolves that item's draft layout + merged tokens; otherwise a
        // param-less page.
        var path = this.page;
        var itemParam = "";
        if (this.bundleId != null) {
            path = "bundle/".concat(this.bundleId, "/").concat(this.bundleKey || "bundle");
            itemParam = "&bundleId=".concat(this.bundleId);
        }
        else if (this.syndicateId != null) {
            path = "syndicate/".concat(this.syndicateBrandLotteryId, "/").concat(this.syndicateId, "/").concat(this.syndicateKey || "syndicate");
            itemParam = "&syndicateId=".concat(this.syndicateId);
        }
        else if (this.lotteryId != null) {
            path = "lottery/".concat(this.lotteryBrandLotteryId, "/").concat(this.lotteryId, "/").concat(this.lotteryKey || "lottery");
            itemParam = "&brandLotteryId=".concat(this.lotteryBrandLotteryId);
        }
        var editParam = this.editText ? "&editText=true" : "";
        var href = "".concat(window.location.origin, "/preview/").concat(path, "?lang=").concat(this.lang || "en") +
            "&brandId=".concat(this.brandId, "&templateName=").concat(this.templateName, "&themeName=").concat(theme, "&isOverlay=false").concat(itemParam).concat(editParam);
        if (href === this.routeToNavigate && !force)
            return;
        this.routeToNavigate = href;
        for (var i = this.container.children.length - 1; i >= 0; i--)
            this.container.removeChild(this.container.children.item(i));
        bioMp(this.container, { url: href, type: this.screenType });
        if (this.editText)
            this.attachInlineTextEdit();
    };
    RenderPreview.prototype.iframeDocument = function () {
        var iframe = this.container ? this.container.querySelector("iframe") : null;
        return iframe ? iframe.contentDocument : null;
    };
    RenderPreview.prototype.ensureHighlightStyle = function (doc) {
        if (doc.getElementById(RenderPreview_1.HighlightStyleId))
            return;
        var style = doc.createElement("style");
        style.id = RenderPreview_1.HighlightStyleId;
        style.textContent = "." + RenderPreview_1.HighlightClass +
            "{outline:3px solid #2961A7 !important;outline-offset:-3px;}";
        (doc.head || doc.documentElement).appendChild(style);
    };
    RenderPreview.prototype.highlightSection = function (sectionName) {
        try {
            var doc = this.iframeDocument();
            if (!doc || !sectionName)
                return;
            this.clearHighlight();
            this.ensureHighlightStyle(doc);
            var matches = doc.querySelectorAll('[data-render-section="' + sectionName + '"]');
            for (var i = 0; i < matches.length; i++)
                matches[i].classList.add(RenderPreview_1.HighlightClass);
            if (matches.length > 0)
                this.scrollIntoIframeView(doc, matches[0]);
        }
        catch (_a) {
            /* cross-origin or iframe not ready - ignore */
        }
    };
    RenderPreview.prototype.highlightInstance = function (instanceId) {
        try {
            var doc = this.iframeDocument();
            if (!doc || !instanceId)
                return;
            this.clearHighlight();
            this.ensureHighlightStyle(doc);
            var el = doc.querySelector('[data-instance-id="' + instanceId + '"]');
            if (!el)
                return;
            var target = (el.tagName.toLowerCase() === "compose" ? (el.firstElementChild || el) : el);
            target.classList.add(RenderPreview_1.HighlightClass);
            this.scrollIntoIframeView(doc, target);
        }
        catch (_a) {
            /* cross-origin or iframe not ready - ignore */
        }
    };
    RenderPreview.prototype.highlightVariant = function (variantId) {
        try {
            var doc = this.iframeDocument();
            if (!doc || !variantId)
                return;
            this.clearHighlight();
            this.ensureHighlightStyle(doc);
            var matches = doc.querySelectorAll('[data-variant="' + variantId + '"]');
            for (var i = 0; i < matches.length; i++)
                matches[i].classList.add(RenderPreview_1.HighlightClass);
            if (matches.length > 0)
                this.scrollIntoIframeView(doc, matches[0]);
        }
        catch (_a) {
            /* cross-origin or iframe not ready - ignore */
        }
    };
    /** Highlight a single placed instance: the index-th element of this variant in document order
        (the preview renders same-variant blocks in the same order the editor lists them). Falls back
        to highlighting every instance when the index can't be resolved. */
    RenderPreview.prototype.highlightVariantInstance = function (variantId, index) {
        try {
            var doc = this.iframeDocument();
            if (!doc || !variantId)
                return;
            var matches = doc.querySelectorAll('[data-variant="' + variantId + '"]');
            if (index == null || index < 0 || index >= matches.length) {
                this.highlightVariant(variantId);
                return;
            }
            this.clearHighlight();
            this.ensureHighlightStyle(doc);
            var el = matches[index];
            el.classList.add(RenderPreview_1.HighlightClass);
            this.scrollIntoIframeView(doc, el);
        }
        catch (_a) {
            /* cross-origin or iframe not ready - ignore */
        }
    };
    // Scroll the element into view WITHIN the preview only. scrollIntoView() crosses the iframe boundary
    // and also scrolls the outer preview pane, centering the tall device frame and pushing its top out of
    // reach - so we scroll the preview's own scroller manually instead. On tall pages (e.g. the lottery
    // page) the SPA reports its full height back to bioMp, which resizes the iframe to its content height;
    // the iframe document then has no internal overflow and the real scroller is the bioMp device-frame
    // wrapper (the iframe's parent). Prefer that wrapper; fall back to the iframe's own scroller.
    RenderPreview.prototype.scrollIntoIframeView = function (doc, el) {
        try {
            var rect = el.getBoundingClientRect();
            var iframe = this.container ? this.container.querySelector("iframe") : null;
            var wrapper = iframe ? iframe.parentElement : null;
            if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) {
                // The iframe is sized to its content (not internally scrolled), so rect.top - measured
                // against the iframe's own viewport - is the element's absolute offset from the iframe top.
                // The iframe sits at iframe.offsetTop within the wrapper, giving its absolute scroll target.
                var top_1 = rect.top + iframe.offsetTop - (wrapper.clientHeight / 2) + (rect.height / 2);
                wrapper.scrollTo({ top: Math.max(0, top_1), behavior: "smooth" });
                return;
            }
            var scroller = (doc.scrollingElement || doc.documentElement);
            var viewH = doc.documentElement.clientHeight || scroller.clientHeight || 0;
            var top_2 = scroller.scrollTop + rect.top - (viewH / 2) + (rect.height / 2);
            scroller.scrollTo({ top: Math.max(0, top_2), behavior: "smooth" });
        }
        catch (_a) {
            /* cross-origin or iframe not ready - ignore */
        }
    };
    RenderPreview.prototype.clearHighlight = function () {
        try {
            var doc = this.iframeDocument();
            if (!doc)
                return;
            var highlighted = doc.querySelectorAll("." + RenderPreview_1.HighlightClass);
            for (var i = 0; i < highlighted.length; i++)
                highlighted[i].classList.remove(RenderPreview_1.HighlightClass);
        }
        catch (_a) {
            /* ignore */
        }
    };
    RenderPreview.prototype.flashToken = function (varName) {
        var _this = this;
        try {
            var doc = this.iframeDocument();
            if (!doc || !varName || this.flashedVar === varName)
                return;
            this.clearTokenFlash();
            this.flashedVar = varName;
            this.flashIndex = 0;
            this.applyFlash();
            this.flashTimer = setInterval(function () { return _this.applyFlash(); }, RenderPreview_1.FlashIntervalMs);
        }
        catch (_a) {
            /* cross-origin or iframe not ready - ignore */
        }
    };
    // Where to set the flashed variable. :root covers pages whose tokens come from the brand :root block;
    // per-item pages (bundle/syndicate/lottery) get their tokens from a scoped `.page-*.…-<id>` rule that
    // OVERRIDES :root, so we must also set it on those page roots (a closer ancestor wins the cascade).
    RenderPreview.prototype.flashTargets = function (doc) {
        var targets = [doc.documentElement];
        var scoped = doc.querySelectorAll(".page-bundle, .page-syndicate, .page-lottery");
        for (var i = 0; i < scoped.length; i++)
            targets.push(scoped[i]);
        return targets;
    };
    RenderPreview.prototype.applyFlash = function () {
        var _this = this;
        var doc = this.iframeDocument();
        if (!doc || !this.flashedVar)
            return;
        var color = RenderPreview_1.FlashColors[this.flashIndex % RenderPreview_1.FlashColors.length];
        this.flashTargets(doc).forEach(function (el) { return el.style.setProperty(_this.flashedVar, color, "important"); });
        this.flashIndex++;
    };
    RenderPreview.prototype.clearTokenFlash = function () {
        var _this = this;
        if (this.flashTimer) {
            clearInterval(this.flashTimer);
            this.flashTimer = null;
        }
        try {
            var doc = this.iframeDocument();
            if (doc && this.flashedVar)
                this.flashTargets(doc).forEach(function (el) { return el.style.removeProperty(_this.flashedVar); });
        }
        catch (_a) {
            /* ignore */
        }
        finally {
            this.flashedVar = null;
        }
    };
    // Each reload builds a fresh iframe; wire the inline editor once its document loads. Delegated
    // listeners on the document catch text rendered later by the SPA.
    RenderPreview.prototype.attachInlineTextEdit = function () {
        var _this = this;
        var iframe = this.container ? this.container.querySelector("iframe") : null;
        if (!iframe)
            return;
        iframe.addEventListener("load", function () {
            try {
                if (iframe.contentDocument && iframe.contentWindow)
                    _this.setupInlineEdit(iframe.contentDocument, iframe.contentWindow);
            }
            catch (_a) {
                /* cross-origin or not ready - ignore */
            }
        });
    };
    RenderPreview.prototype.setupInlineEdit = function (doc, win) {
        var _this = this;
        if (!doc.getElementById(RenderPreview_1.EditStyleId)) {
            var style = doc.createElement("style");
            style.id = RenderPreview_1.EditStyleId;
            style.textContent =
                "." + RenderPreview_1.EditableClass + "{outline:2px dashed #2961A7 !important;outline-offset:1px;cursor:text;}" +
                    ".render-preview-edit-input{position:fixed;z-index:2147483647;box-sizing:border-box;" +
                    "border:2px solid #2961A7;border-radius:3px;padding:2px 6px;font:inherit;background:#fff;color:#111;}";
            (doc.head || doc.documentElement).appendChild(style);
        }
        doc.addEventListener("mouseover", function (e) {
            if (_this.navigate) // Navigate sub-mode: no edit affordance, let the page behave normally
                return;
            _this.setHoverEditable(_this.editableElementAt(win, e.target));
        }, true);
        doc.addEventListener("click", function (e) {
            if (_this.navigate) // Navigate sub-mode: pass the click through to the page (open menus/dialogs)
                return;
            var el = _this.editableElementAt(win, e.target);
            if (!el)
                return;
            e.preventDefault();
            e.stopPropagation();
            _this.openInlineEditor(doc, win, el);
        }, true);
    };
    // The tagged string renders as a direct child text node of the element bound to `& t`; find it by
    // walking up from the click target a few levels.
    RenderPreview.prototype.taggedTextNode = function (win, el) {
        if (!el || !el.childNodes)
            return null;
        for (var i = 0; i < el.childNodes.length; i++) {
            var n = el.childNodes[i];
            if (n.nodeType === 3 && readTag(win, n.nodeValue))
                return n;
        }
        return null;
    };
    RenderPreview.prototype.editableElementAt = function (win, target) {
        var el = target;
        var depth = 0;
        while (el && depth < 5) {
            if (this.taggedTextNode(win, el))
                return el;
            el = el.parentElement;
            depth++;
        }
        return null;
    };
    RenderPreview.prototype.setHoverEditable = function (el) {
        if (this.hoverEl === el)
            return;
        if (this.hoverEl)
            this.hoverEl.classList.remove(RenderPreview_1.EditableClass);
        this.hoverEl = el;
        if (el)
            el.classList.add(RenderPreview_1.EditableClass);
    };
    RenderPreview.prototype.openInlineEditor = function (doc, win, el) {
        var _this = this;
        var node = this.taggedTextNode(win, el);
        var tag = node ? readTag(win, node.nodeValue) : null;
        if (!tag)
            return;
        this.closeInlineEditor();
        var rect = el.getBoundingClientRect();
        var input = doc.createElement("input");
        input.type = "text";
        input.className = "render-preview-edit-input";
        input.value = tag.value;
        input.style.left = rect.left + "px";
        input.style.top = rect.top + "px";
        input.style.width = Math.max(rect.width, 120) + "px";
        doc.body.appendChild(input);
        input.focus();
        input.select();
        this.inlineInput = input;
        var done = false;
        var commit = function () {
            if (done)
                return;
            done = true;
            var newValue = input.value;
            _this.closeInlineEditor();
            if (newValue === tag.value)
                return;
            // keep any text before the marker (e.g. a price)
            node.nodeValue = node.nodeValue.substring(0, tag.index) + tagText(win, tag.key, newValue);
            if (_this.onTextEdit)
                _this.onTextEdit({ key: tag.key, value: newValue });
        };
        input.addEventListener("keydown", function (ke) {
            if (ke.key === "Enter") {
                ke.preventDefault();
                commit();
            }
            else if (ke.key === "Escape") {
                ke.preventDefault();
                done = true;
                _this.closeInlineEditor();
            }
        });
        input.addEventListener("blur", function () { return commit(); });
    };
    RenderPreview.prototype.closeInlineEditor = function () {
        if (this.inlineInput && this.inlineInput.parentNode)
            this.inlineInput.parentNode.removeChild(this.inlineInput);
        this.inlineInput = null;
    };
    RenderPreview.prototype.detached = function () {
        this.clearTokenFlash();
        this.closeInlineEditor();
    };
    var RenderPreview_1;
    RenderPreview.HighlightStyleId = "render-preview-highlight-style";
    RenderPreview.HighlightClass = "render-preview-highlighted";
    RenderPreview.FlashColors = ["#ff00ff", "#00e5ff"];
    RenderPreview.FlashIntervalMs = 450;
    // ---- Inline text editing (edit mode) ----
    RenderPreview.EditStyleId = "render-preview-edit-style";
    RenderPreview.EditableClass = "render-preview-editable";
    __decorate([
        bindable,
        __metadata("design:type", Number)
    ], RenderPreview.prototype, "brandId", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], RenderPreview.prototype, "templateName", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], RenderPreview.prototype, "themeName", void 0);
    __decorate([
        bindable({ defaultBindingMode: bindingMode.twoWay }),
        __metadata("design:type", String)
    ], RenderPreview.prototype, "lang", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Array)
    ], RenderPreview.prototype, "langs", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], RenderPreview.prototype, "page", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Boolean)
    ], RenderPreview.prototype, "showPagePicker", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Object)
    ], RenderPreview.prototype, "capabilities", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Boolean)
    ], RenderPreview.prototype, "hideTitle", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Function)
    ], RenderPreview.prototype, "pageChange", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Number)
    ], RenderPreview.prototype, "bundleId", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], RenderPreview.prototype, "bundleKey", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Number)
    ], RenderPreview.prototype, "syndicateId", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], RenderPreview.prototype, "syndicateKey", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Number)
    ], RenderPreview.prototype, "syndicateBrandLotteryId", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Number)
    ], RenderPreview.prototype, "lotteryId", void 0);
    __decorate([
        bindable,
        __metadata("design:type", String)
    ], RenderPreview.prototype, "lotteryKey", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Number)
    ], RenderPreview.prototype, "lotteryBrandLotteryId", void 0);
    __decorate([
        bindable({ defaultBindingMode: bindingMode.twoWay }),
        __metadata("design:type", Boolean)
    ], RenderPreview.prototype, "editText", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Boolean)
    ], RenderPreview.prototype, "editable", void 0);
    __decorate([
        bindable,
        __metadata("design:type", Function)
    ], RenderPreview.prototype, "onTextEdit", void 0);
    RenderPreview = RenderPreview_1 = __decorate([
        autoinject,
        transient()
    ], RenderPreview);
    return RenderPreview;
}());
export { RenderPreview };
